import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ErrorLoggingService } from '../services/error-logging.service';
import { ErrorHandlingService } from '../services/error-handling.service';
import { AuthService } from '../services/auth.service';

const FIREBASE_ID_TOKEN_EXPIRED_MESSAGE =
  'Firebase ID token has expired. Get a fresh ID token from your client app and try again (auth/id-token-expired). See https://firebase.google.com/docs/auth/admin/verify-id-tokens for details on how to retrieve an ID token.';

// Marks a request that has already been retried after a token refresh, so a
// second 401 falls through to logout instead of looping forever.
const RETRIED_HEADER = 'X-Token-Refreshed';

@Injectable()
export class ErrorLoggingInterceptor implements HttpInterceptor {
  constructor(
    private errorLoggingService: ErrorLoggingService,
    private errorHandlingService: ErrorHandlingService,
    private authService: AuthService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const message = error.error?.message;
        const isTokenExpiredMessage =
          typeof message === 'string' && message === FIREBASE_ID_TOKEN_EXPIRED_MESSAGE;
        const isUnauthorized = error.status === 401;
        if (isTokenExpiredMessage || isUnauthorized) {
          // Already retried once with a fresh token, or this IS the refresh
          // endpoint — give up and log the user out.
          if (req.headers.has(RETRIED_HEADER)) {
            this.errorHandlingService.errorHandling(this.logoutMessage(isTokenExpiredMessage, message));
            return throwError(error);
          }

          // Try to silently refresh the token and replay the request once, so
          // the user stays logged in past the ~1h Firebase token expiry.
          return from(this.authService.refreshIdToken()).pipe(
            switchMap((freshToken) => {
              if (!freshToken) {
                this.errorHandlingService.errorHandling(
                  this.logoutMessage(isTokenExpiredMessage, message)
                );
                return throwError(error);
              }
              const retried = req.clone({
                setHeaders: {
                  authorization: 'Bearer ' + freshToken,
                  [RETRIED_HEADER]: '1',
                },
              });
              return next.handle(retried);
            })
          );
        }

        // Don't log errors from the logging endpoint itself to prevent loops
        if (req.url.includes('log-issue')) {
          console.warn('⚠️ Skipping logging for log-issue endpoint to prevent recursion');
          return throwError(error);
        }

        // Enhanced error logging with stack trace
        console.group('🚨 HTTP Error Interceptor');
        console.error('Request URL:', req.url);
        console.error('Request Method:', req.method);
        console.error('Error Status:', error.status);
        console.error('Error Message:', error.message);
        console.error('Error Details:', error.error);
        console.error('Full Error Object:', error);
        console.error('Stack Trace:', (error as any).stack);
        console.error('Timestamp:', new Date().toISOString());
        console.groupEnd();

        // Send HTTP error to logging service (non-blocking)
        this.errorLoggingService.logHttpError(
          `HTTP ${error.status}: ${error.message}`,
          error,
          req.url,
          req.method,
          req.body
        ).catch(loggingError => {
          console.error('Failed to log HTTP error:', loggingError);
        });
        
        return throwError(error);
      })
    );
  }

  private logoutMessage(isTokenExpiredMessage: boolean, message: string): string {
    return isTokenExpiredMessage ? message : 'Token expired. Please refresh your authentication.';
  }
}
